<template>
  <div v-if="company">
    <Dialog
      modal
      :visible="visible"
      @update:visible="visible = $event"
      class="max-md:w-full"
      :pt="{
        header: { style: ' display:none' },
        content: { style: 'padding: 0 !important;border-radius: 10px ' },
      }"
    >
      <div
        class="relative flex h-full flex-col justify-between p-10"
        v-if="!company.mobile_verified_at"
      >
        <button
          class="bg-background-3 text-primary absolute right-5 top-5 flex h-8 w-8 items-center justify-center rounded-full"
          @click="visible = false"
        >
          <span class="font-bold">X</span>
        </button>

        <div class="mb-5 flex items-center justify-center">
          <section-title>Vérification de votre numéro</section-title>
        </div>
        <div class="mb-10 text-center">
          Pour accéder aux fonctionnalités de votre compte, vous devez
          valider<br />
          votre numéro. Cette étape est nécessaire pour nous assurer que vous
          êtes bien<br />
          joignable par téléphone et que vous étes bien le propriétaire du
          numéro.
        </div>

        <Message severity="error" v-if="error">
          {{ error }}
        </Message>

        <FormField
          class="mb-5"
          name="mobile"
          label="Téléphone mobile"
          :errors="validationErrors"
        >
          <InputMask
            placeholder="Téléphone mobile"
            mask="+33 (9)9 99 99 99 99"
            unmask
            v-model="mobile"
            :class="`w-full ${validationErrors.mobile ? 'p-invalid' : ''}`"
            :disabled="sendLoading"
          ></InputMask>

          <small
            >Vous devez utiliser le format international. Exemple: Si votre
            numéro est 06.12.34.34.34, entrez +33 (0)6 12 34 34 34</small
          >
        </FormField>
        <rounded-button
          @click="sendVerificationCode"
          design="3"
          class="mx-auto mb-10 w-[25rem]"
          :loading="sendLoading"
        >
          {{
            codeSent
              ? "Renvoyer le code de validation"
              : "Envoyer le code de validation"
          }}
        </rounded-button>
        <div v-if="codeSent">
          <FormField
            class="mb-5 mt-10"
            name="code"
            label="Code de validation"
            :errors="validationErrors"
          >
            <InputMask
              placeholder="Code de validation"
              mask="999999"
              v-model="validationCode"
              :class="`w-full ${validationErrors.code ? 'p-invalid' : ''}`"
              :disabled="checkLoading"
            ></InputMask>

            <small
              >Veuillez saisir le code de validation que vous avez reçu par SMS
              (Valable 15 minutes)
            </small>
          </FormField>

          <rounded-button
            @click="checkCode"
            design="4"
            :loading="checkLoading"
            class="mx-auto mb-10 w-[25rem]"
          >
            Valider
          </rounded-button>
        </div>
      </div>
      <div class="flex h-full flex-col justify-between p-10" v-else>
        <div class="h-75 flex items-center justify-center">
          <div
            class="align-content-center flex flex-col items-center justify-center"
          >
            <icon
              icon="check"
              class="text-secondary border-secondary mb-10 h-[15rem] w-[15rem] rounded-full border-[1rem] p-10"
            ></icon>

            <h2 class="text-secondary mb-8 text-center text-3xl font-bold">
              Numéro vérifié avec succès
            </h2>
          </div>
        </div>
      </div>
    </Dialog>
  </div>
</template>
<script setup>
import { ref } from "vue";

import { useAuthStore } from "~/store/auth";

// Define props

const emit = defineEmits(["store", "update:visible"]);
const { $api, $event, $listen, $unListen, $toast } = useNuxtApp();
const dayjs = useDayjs();

const sendLoading = ref(false);
const checkLoading = ref(false);
const codeSent = ref(false);

const validationErrors = ref({});
const error = ref(null);
const authStore = useAuthStore();
const visible = ref(false);

const { company } = authStore;

const mobile = ref("");

const prefillMobile = () => {
  mobile.value = company?.mobile;
};

const onOpenMobileModal = () => {
  visible.value = true;
};

onMounted(() => {
  $listen("open-mobile-modal", onOpenMobileModal);
});

onBeforeUnmount(() => {
  $unListen("open-mobile-modal", onOpenMobileModal);
});

watch(visible, (visible) => {
  if (!visible) {
    // reset

    sendLoading.value = false;
    checkLoading.value = false;
    codeSent.value = false;
    validationErrors.value = {};
    error.value = null;
    mobile.value = "";
    validationCode.value = null;
    prefillMobile();
  }
});

watch(
  () => company,
  (company) => {
    prefillMobile();
  },
  { immediate: true, deep: true }
);

const validationCode = ref(null);

const sendVerificationCode = () => {
  sendLoading.value = true;
  error.value = null;
  validationErrors.value = {};

  return $api
    .call("post", $api.route("me.company.send-code"), {
      mobile: mobile.value,
    })
    .then((response) => {
      $toast.success("Code de validation envoyé avec succès");
      $event("company.updated", response);

      codeSent.value = true;
    })
    .catch((err) => {
      $toast.error(
        "Une erreur est survenue lors de l'envoi du code de validation"
      );

      if (err.response) {
        if (err.response.status == 422) {
          validationErrors.value = err.response.data.errors;
        } else {
          error.value = err.response.data.message;
        }
      } else {
        error.value = "Une erreur est survenue";
      }
    })
    .finally(() => {
      sendLoading.value = false;
    });
};

const checkCode = () => {
  checkLoading.value = true;
  error.value = null;
  validationErrors.value = {};

  return $api
    .call("post", $api.route("me.company.verify-code"), {
      code: validationCode.value,
    })
    .then(() => {
      $toast.success("Numéro de téléphone validé avec succès");

      $event("company.updated");

      return authStore.fetchCompany();
    })
    .then(() => {
      setTimeout(() => {
        visible.value = false;
      }, 2000);
    })
    .catch((err) => {
      if (err.response) {
        if (err.response?.status == 422) {
          validationErrors.value = err.response.data.errors;
        }

        if (err.response?.data?.message) {
          $toast.error(err.response.data.message);
          error.value = err.response.data.message;
        }
      } else {
        error.value = err;
      }
    })
    .finally(() => {
      checkLoading.value = false;
    });
};
</script>
